import { AdminFeedbackCycle } from '~src/hooks/api/adminFeedback/types';
import {
  ButtonContainer,
  CustomDatePickerContainer,
  DateInputContainer,
  FieldContainer,
  SettingsTabContainer,
} from './SettingsTab.styled';
import { Field, Form } from 'react-final-form';
import { isDueDateBeforeStartDate, validatorRequired } from '~src/services/validators';
import { FieldType } from '~src/types';
import CustomFormInput from '~src/shared/reactFinalForm/FormInput';
import PeopleChips from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { Segment } from '~src/interfaces/segment.interface';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '~src/shared/form/inputs/CustomDatePicker';
import moment from 'moment';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import { translateContent } from '~src/services/translationServices';
import useUpdateAdminFeedbackCycle from '~src/hooks/api/adminFeedback/useUpdateAdminFeedbackCycle';
import { useDispatch } from 'react-redux';
import { showNotification } from '~src/store/app/actions';
import { AxiosError } from 'axios';
import feedbackCycleCache from '~src/hooks/api/adminFeedback/cache';
import { useEffect, useRef } from 'react';
import { MainContainerAnimation } from '~src/styles/animations';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import {useIsoDateFormatter} from "~src/services/hooks/useDateFormatter";

interface SettingsTabProps {
  adminFeedbackCycle?: AdminFeedbackCycle;
}

interface FormData {
  name: string;
  userIds: Segment[];
  cycleStartDate: moment.Moment;
  cycleDueDate: moment.Moment;
  meetingStartDate: moment.Moment;
  meetingDueDate: moment.Moment;
}

const SettingsTab = ({ adminFeedbackCycle }: SettingsTabProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isoDateFormatter = useIsoDateFormatter();

  const isDraft = useRef<boolean | null>(null);

  const { mutate: updateFeedbackCycle, error: apiError } = useUpdateAdminFeedbackCycle();

  useEffect(() => {
    isDraft.current = adminFeedbackCycle?.active || null;
  }, [adminFeedbackCycle]);

  const onFormSubmit = (data: FormData) => {
    const translatedName = adminFeedbackCycle?.name ? translateContent(adminFeedbackCycle.name) : '';
    const hasNameChanged = data.name !== translatedName;

    updateFeedbackCycle(
      {
        id: adminFeedbackCycle?.id || '',
        ...(hasNameChanged ? { name: { en_US: data.name } } : {}),
        userIds: data.userIds.map((item) => item.id),
        active: !isDraft.current,
        cycleStartDate: isoDateFormatter(data.cycleStartDate),
        cycleDueDate: isoDateFormatter(data.cycleDueDate),
        meetingStartDate: isoDateFormatter(data.meetingStartDate),
        meetingDueDate: isoDateFormatter(data.meetingDueDate),
      },
      {
        onSuccess: (responseData) => {
          feedbackCycleCache.updateFeedbackCycle(responseData);
          dispatch(
            showNotification({
              id: 'admin_feedback_cycle_updated',
              txtKey: 'x.performance_management.feedback_updated',
            }),
          );
        },
      },
    );
  };

  const errorResponse = (
    apiError as AxiosError<
      {
        arguments: {
          field_name: string;
        };
        translation_key: string;
      }[]
    >
  )?.response;

  const notUniqueNameError = errorResponse?.data.find((item) => item.arguments?.field_name.includes('name.'));

  const draftButtonTranslationKey = adminFeedbackCycle?.active
    ? 'x.performance_management.button.save.as.draft'
    : 'x.button.save';

  const activeButtonTranslationKey = adminFeedbackCycle?.active
    ? 'x.button.save'
    : 'x.button.publish.changes';

  if (!adminFeedbackCycle) {
    return (
      <SettingsTabContainer>
        <LoadingSpinner />
      </SettingsTabContainer>
    );
  }

  return (
    <SettingsTabContainer>
      <MainContainerAnimation>
        <Form
          onSubmit={onFormSubmit}
          keepDirtyOnReinitialize
          initialValues={{
            name: adminFeedbackCycle?.name ? translateContent(adminFeedbackCycle?.name) : '',
            userIds: adminFeedbackCycle?.users || [],
            cycleStartDate: moment(adminFeedbackCycle?.cycleStartDate),
            cycleDueDate: moment(adminFeedbackCycle?.cycleDueDate),
            meetingStartDate: moment(adminFeedbackCycle?.meetingStartDate),
            meetingDueDate: moment(adminFeedbackCycle?.meetingDueDate),
          }}
          render={({ handleSubmit, pristine, values, form }) => {
            const isDraftButtonDisabled = adminFeedbackCycle?.active ? false : pristine;
            const isActiveButtonDisabled = adminFeedbackCycle?.active ? pristine : false;

            return (
              <form onSubmit={handleSubmit}>
                <FieldContainer>
                  <Field
                    name="name"
                    validate={validatorRequired}
                    label={<>{t('x.performance-management.cycle_name')}</>}
                    children={({ input, label, meta: { error, touched } }: FieldType<string>) => (
                      <CustomFormInput
                        {...input}
                        autoFocus
                        label={label}
                        labelKey={label}
                        type={input.type}
                        error={touched ? (error as string) || notUniqueNameError?.translation_key : undefined}
                      />
                    )}
                  />
                </FieldContainer>

                <FieldContainer style={{ marginBottom: '12px' }}>
                  <h4 data-translation="x.performance_manager.assigned_groups">
                    <>{t('x.performance_manager.assigned_groups')}</>
                  </h4>
                  <Field
                    name="userIds"
                    validate={validatorRequired}
                    label={<>{t('x.performance_manager.assigned_groups')}</>}
                    children={({ input, label, meta: { error, touched } }: FieldType<Segment[]>) => (
                      <PeopleChips
                        placeHolderKey={`x.survey.performance_manager.chips.placeholder`}
                        fetchSuggestions={fetchSearchSegments}
                        chipsName={label}
                        selectedPeople={input.value || []}
                        onChangePeople={input.onChange}
                        error={touched && !!error}
                      />
                    )}
                  />
                </FieldContainer>

                <FieldContainer>
                  <h4 data-translation="x.performance_manager.cycle_range.title">
                    <>{t('x.performance_manager.cycle_range.title')}</>
                  </h4>
                  <DateInputContainer>
                    <Field
                      name="cycleStartDate"
                      validate={(value: moment.Moment) => {
                        if (!value) {
                          return validatorRequired(value) as string;
                        }
                        return isDueDateBeforeStartDate(value, values.cycleDueDate);
                      }}
                      label={<>{t('x.performance_manager.cycle_range.start')}</>}
                      children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                        <CustomDatePickerContainer>
                          <CustomDatePicker
                            {...input}
                            dateValue={input.value}
                            error={touched ? (error as string) : undefined}
                            allowClear={false}
                            label={label}
                            onChange={(value) => {
                              input.onChange(value);
                              form.resetFieldState('cycleDueDate');
                            }}
                            labelKey={label}
                            datePickerOptions={{
                              hideDisabledOptions: true,
                              disabledDate: (d) =>
                                d.toDate().getTime() > moment().add(10, 'years').toDate().getTime(),
                            }}
                          />
                        </CustomDatePickerContainer>
                      )}
                    />
                    <Field
                      name="cycleDueDate"
                      validate={(value: moment.Moment) => {
                        if (!value) {
                          return validatorRequired(value) as string;
                        }
                        return isDueDateBeforeStartDate(values.cycleStartDate, value);
                      }}
                      label={<>{t('x.performance_manager.cycle_range.due')}</>}
                      children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                        <CustomDatePickerContainer>
                          <CustomDatePicker
                            {...input}
                            dateValue={input.value}
                            error={touched ? (error as string) : undefined}
                            allowClear={false}
                            label={label}
                            onChange={(value) => {
                              input.onChange(value);
                              form.resetFieldState('cycleStartDate');
                            }}
                            labelKey={label}
                            datePickerOptions={{
                              hideDisabledOptions: true,
                              disabledDate: (d) =>
                                d.toDate().getTime() > moment().add(10, 'years').toDate().getTime(),
                            }}
                          />
                        </CustomDatePickerContainer>
                      )}
                    />
                  </DateInputContainer>
                </FieldContainer>
                <FieldContainer>
                  <h4 data-translation="x.performance_manager.meeting_range.title">
                    <>{t('x.performance_manager.meeting_range.title')}</>
                  </h4>
                  <DateInputContainer>
                    <Field
                      name="meetingStartDate"
                      validate={(value: moment.Moment) => {
                        if (!value) {
                          return validatorRequired(value) as string;
                        }
                        return isDueDateBeforeStartDate(value, values.meetingDueDate);
                      }}
                      label={<>{t('x.performance_manager.meeting_range.start')}</>}
                      children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                        <CustomDatePickerContainer>
                          <CustomDatePicker
                            {...input}
                            dateValue={input.value}
                            error={touched ? (error as string) : undefined}
                            allowClear={false}
                            onChange={(value) => {
                              input.onChange(value);
                              form.resetFieldState('meetingDueDate');
                            }}
                            label={label}
                            labelKey={label}
                            datePickerOptions={{
                              disabledDate: (d) => d.toDate().getTime() < moment().toDate().getTime(),
                            }}
                          />
                        </CustomDatePickerContainer>
                      )}
                    />
                    <Field
                      name="meetingDueDate"
                      validate={(value: moment.Moment) => {
                        if (!value) {
                          return validatorRequired(value) as string;
                        }
                        return isDueDateBeforeStartDate(values.meetingStartDate, value);
                      }}
                      label={<>{t('x.performance_manager.meeting_range.due')}</>}
                      children={({ input, label, meta: { error, touched } }: FieldType<moment.Moment>) => (
                        <CustomDatePickerContainer>
                          <CustomDatePicker
                            {...input}
                            dateValue={input.value}
                            error={touched ? (error as string) : undefined}
                            allowClear={false}
                            label={label}
                            onChange={(value) => {
                              input.onChange(value);
                              form.resetFieldState('meetingStartDate');
                            }}
                            labelKey={label}
                            datePickerOptions={{
                              disabledDate: (d) => d.toDate().getTime() < moment().toDate().getTime(),
                            }}
                          />
                        </CustomDatePickerContainer>
                      )}
                    />
                  </DateInputContainer>
                </FieldContainer>

                <ButtonContainer>
                  <SecondaryButton
                    onClick={() => {
                      isDraft.current = true;
                    }}
                    type="submit"
                    style={{ marginRight: '10px' }}
                    disabled={isDraftButtonDisabled}
                  >
                    <span data-translation={draftButtonTranslationKey}>
                      <>{t(draftButtonTranslationKey)}</>
                    </span>
                  </SecondaryButton>

                  <PrimaryButtonLoading
                    onClick={() => {
                      isDraft.current = false;
                    }}
                    type="submit"
                    disabled={isActiveButtonDisabled}
                    isLoading={false}
                  >
                    <span data-translation={activeButtonTranslationKey}>
                      <>{t(activeButtonTranslationKey)}</>
                    </span>
                  </PrimaryButtonLoading>
                </ButtonContainer>
              </form>
            );
          }}
        />
      </MainContainerAnimation>
    </SettingsTabContainer>
  );
};

export default SettingsTab;
